import _ from 'lodash';

import { deepFreeze } from 'shared/util';

// The games Raidbots serves, as data (specs/wow-forever). A game is a WoW
// ruleset: modern, or WoW Forever (the Classic-era ruleset on a 1.60 client).
// Each Raidbots env (shared/envs) belongs to one game; each deployed site is
// one game, chosen by RAIDBOTS_ENV. Everything that differs between the two —
// which tools exist, which SimC channel family runs the sims, which item
// systems the UI shows — hangs off this table, never off a realm or host name.
//
// features is the capability object the tool code gates on. Forever's list is
// what phase 0 knows; later phases flip entries as the client and the SimC
// branch settle.
// The three site colors that differ per game: page ground, the darker panel
// (DarkerBox and friends), and body text. Everything else (accents, class and
// item colors, the orange headings) is shared. Forever's come from the WoW
// Forever logo: a deep navy-teal ground, a darker panel, parchment text; the
// warm gold is its header accent.
export const GAMES = deepFreeze({
  modern: {
    key: 'modern',
    name: 'Modern WoW',
    maxLevel: 90,
    // The expansion whose data is current, for the generator (shared/constants EXPANSIONS).
    expansion: 'MIDNIGHT',
    theme: {
      ground: '#333339',
      panel: '#292929',
      text: '#eee',
      accent: '#7a76ee',
    },
    tools: ['quick', 'stats', 'gear', 'talents', 'optimize', 'droptimizer', 'advanced'],
    // The GitHub repository the game's SimC builds and data PRs come from.
    simcRepo: 'simulationcraft/simc',
    simcChannels: ['weekly', 'nightly', 'beta', 'latest'],
    defaultSimcChannel: 'weekly',
    // The SimC addon major a paste must be at least. Modern's rule is the
    // current expansion's client major (shared/constants).
    addonMinMajor: null,
    // What the Simulation Options panel offers and simcinput emits: the raid
    // buff toggles (override.* lines, plus Power Infusion and Vantus Rune) and
    // whether augment runes are a consumable. Item/expansion options live in
    // shared/expansionOptions/<game>.js.
    simOptions: {
      raidBuffs: [
        'bloodlust', 'arcaneIntellect', 'fortitude', 'markOfTheWild', 'battleShout', 'mysticTouch',
        'chaosBrand', 'skyfury', 'huntersMark', 'powerInfusion', 'bleeding', 'vantusRune',
      ],
      augments: true,
    },
    features: {
      upgrades: true,
      catalyst: true,
      greatVault: true,
      crafting: true,
      embellishments: true,
      sockets: true,
      itemSets: true,
      heroTalents: true,
      dungeonJournal: true,
      armory: true,
      bonusRolls: true,
      mastery: true,
      versatility: true,
      // A SimC paste must fill every gear slot (missing_gear).
      requireFullGear: true,
      // Talent trees are one class tree + one spec tree (+ hero); false means the
      // classic three-tab layout (talents.json `trees`).
      classicTalents: false,
      // Level at which hunter/shaman move leather→mail and warrior/paladin
      // mail→plate; null when a class wears its armor type from level 1.
      armorProficiencyLevel: null,
    },
  },
  forever: {
    key: 'forever',
    name: 'WoW Forever',
    maxLevel: 60,
    expansion: 'VANILLA',
    theme: {
      ground: '#1e2b36',
      panel: '#172128',
      text: '#efe6d3',
      accent: '#c9a66b',
    },
    tools: ['quick', 'advanced'],
    // SimC's own repository for Forever since 2026-10-15; before that, the
    // `forever` branch of simulationcraft/simc (its history continues here).
    simcRepo: 'simulationcraft/simc-forever',
    // Own channel family (shared/simcChannels). No beta or release channel.
    simcChannels: ['latest', 'nightly', 'weekly'],
    // latest until the weekly promotion has a history to stand on.
    defaultSimcChannel: 'latest',
    // The Forever addon reports the modern addon's major (12.x on a 1.60 client).
    addonMinMajor: 12,
    // The classic buffs the panel offers (Dave, 9/29 and 10/3). Not yet: the
    // paladin blessing (one at a time) and the other party-only buffs, which
    // need their own model.
    simOptions: {
      raidBuffs: ['bloodlust', 'arcaneIntellect', 'fortitude', 'markOfTheWild', 'battleShout'],
      augments: false,
    },
    // Profile lines the Forever addon emits beyond the shared set (addon 12.1.5-03).
    // Forwarded to SimC as-is; SimC Forever warns on and skips any it can't parse yet.
    simcInputCommands: [
      'legacy_talents',
      'ranged',
      'ammo',
      'bag1', 'bag2', 'bag3', 'bag4', 'bag5',
      'pet_family', 'pet_creature_id', 'pet_level', 'pet_happiness', 'pet_abilities',
    ],
    features: {
      upgrades: false,
      catalyst: false,
      greatVault: false,
      crafting: false,
      embellishments: false,
      sockets: false,
      itemSets: true,
      heroTalents: false,
      dungeonJournal: false,
      armory: false,
      bonusRolls: false,
      mastery: false,
      versatility: false,
      // Empty slots allowed while Forever gear coverage is still being built out.
      requireFullGear: false,
      classicTalents: true,
      armorProficiencyLevel: 40,
    },
  },
});

export const GAME_KEYS = _.keys(GAMES);

export const isGameKey = key => _.isString(key) && Object.prototype.hasOwnProperty.call(GAMES, key);

// The game this process (or this bundle) is. RAIDBOTS_ENV is the deploy-time
// identity of a site — `forever` on web-forever and in the forever bundle,
// `beta` on web-beta, unset on prod — so it is also the game selector. Read
// per call: the dev stack and tests set it after modules load.
// Body background for the HTML shell, painted before the bundle loads. The
// modern beta site keeps its reddish tint; other games use their theme ground.
const MODERN_BETA_BACKGROUND = '#443339';
export const pageBackground = (gameKey, isBeta = false) => {
  if (gameKey === 'modern' && isBeta) {
    return MODERN_BETA_BACKGROUND;
  }
  return GAMES[gameKey].theme.ground;
};

export const currentGameKey = () => (process.env.RAIDBOTS_ENV === 'forever' ? 'forever' : 'modern');

export const currentGame = () => GAMES[currentGameKey()];

// Persisted rows (sims, setup shares, character snapshots) carry the game of
// the web that wrote them. Both games share one Datastore and one redis, so a
// read path on www must not hand back Forever rows or the reverse. Rows from
// before the field existed are modern.
export const gameOf = row => row?.game || 'modern';
export const isCurrentGame = row => Boolean(row) && gameOf(row) === currentGameKey();

// Whether a per-user list can filter on `game` in the query (a composite index
// on it) instead of in the app. Forever always can: it has no untagged rows
// that matter. Modern can once every untagged row a list could show has aged
// out — `game` has been written on every row since the 2026-10-09 deploy, and
// the lists only show rows younger than the longest retention window after it.
export const UNTAGGED_ROWS_AGED_OUT = Date.parse('2026-11-15T00:00:00Z');
export const canFilterByGame = (game, now = Date.now()) => game !== 'modern' || now >= UNTAGGED_ROWS_AGED_OUT;
